import {
  Box,
  Breadcrumbs,
  Button,
  Typography,
  Paper,
  styled,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Link } from "react-router-dom";
import { ReactNode } from "react";

interface Breadcrumb {
  label: string;
  path?: string;
}

interface PageAction {
  label: string;
  onClick: () => void;
  variant?: "contained" | "outlined" | "text";
  color?: "primary" | "secondary" | "success" | "warning" | "error" | "info";
  icon?: ReactNode;
  disabled?: boolean;
}

interface PageLayoutProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: Breadcrumb[];
  actions?: PageAction[];
  children: ReactNode;
  headerContent?: ReactNode;
}

const PageHeader = styled(Box)({
  marginBottom: 28,
  padding: "24px 28px",
  backgroundColor: "#FFFFFF",
  borderRadius: 16,
  border: "1px solid rgba(226, 232, 240, 0.8)",
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.02)",
  position: "relative" as const,
  overflow: "hidden" as const,
  "&::before": {
    content: '""',
    position: "absolute" as const,
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    background: "linear-gradient(90deg, #D58E4E, rgba(213, 142, 78, 0.2))",
  },
});

const ContentWrapper = styled(Paper)({
  padding: 28,
  borderRadius: 16,
  border: "1px solid rgba(226, 232, 240, 0.8)",
  backgroundColor: "#FFFFFF",
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.02)",
  minHeight: "calc(100vh - 300px)",
});

export const PageLayout = ({
  title,
  subtitle,
  breadcrumbs,
  actions,
  children,
  headerContent,
}: PageLayoutProps) => {
  return (
    <Box>
      {/* Breadcrumbs */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumbs
          separator={
            <NavigateNextIcon fontSize="small" sx={{ color: "#94A3B8" }} />
          }
          aria-label="breadcrumb"
          sx={{ mb: 2 }}
        >
          {breadcrumbs.map((crumb, index) =>
            crumb.path ? (
              <Link
                key={index}
                to={crumb.path}
                style={{
                  textDecoration: "none",
                  color: "#64748B",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                {crumb.label}
              </Link>
            ) : (
              <Typography
                key={index}
                sx={{
                  color: "#1E293B",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                {crumb.label}
              </Typography>
            )
          )}
        </Breadcrumbs>
      )}

      {/* Page Header */}
      <PageHeader>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          {/* Title Section */}
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: "#1E293B",
                mb: subtitle ? 0.5 : 0,
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" sx={{ color: "#64748B" }}>
                {subtitle}
              </Typography>
            )}
          </Box>

          {/* Actions */}
          {actions && actions.length > 0 && (
            <Box display="flex" gap={1.5}>
              {actions.map((action, index) => (
                <Button
                  key={index}
                  variant={action.variant || "contained"}
                  color={action.color || "primary"}
                  onClick={action.onClick}
                  startIcon={action.icon}
                  disabled={action.disabled}
                  size="medium"
                >
                  {action.label}
                </Button>
              ))}
            </Box>
          )}
        </Box>

        {/* Additional Header Content */}
        {headerContent && <Box sx={{ mt: 2 }}>{headerContent}</Box>}
      </PageHeader>

      {/* Page Content */}
      <ContentWrapper elevation={0}>{children}</ContentWrapper>
    </Box>
  );
};

export default PageLayout;

export { };
